import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import { useProfile } from 'context/profile';
import { simpleDate } from 'lib/date';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { LocationEpisodeLinkSet } from 'models/flexible/LocationEpisodeLinkSet';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminLinkedRecordsQueryKeys, adminShowLinkedRecord } from 'services/api/admin/linkedRecords';
import type { AdminLinkSetShowFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';

import { InfoBanner, RecordCard, Scenario, SCENARIOS, SlideoutFooter } from './linkDetailsSlideout/index';

type Props = {
  record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>> | null;
  onClose: () => void;
  onUnlink: (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>) => void;
  onRelink: (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>) => void;
  onRemoveLink?: (link: LocationEpisodeLink) => void;
  onRelinkLink?: (link: LocationEpisodeLink) => void;
  onApplyChanges?: (approveIds: string[], removeIds: string[]) => void;
};

const getScenario = (record: LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>): Scenario => {
  if (record.isUnlinked) return SCENARIOS.UNLINKED;

  const pendingCount = record.pendingLinks.length;

  if (pendingCount > 0) return SCENARIOS.PENDING_REVIEW;

  return record.activeLinks.length === 2 ? SCENARIOS.UNLINK_TWO : SCENARIOS.REMOVE_INDIVIDUAL;
};

const getLabel = (index: number) => String.fromCharCode(65 + index);

export default function LinkDetailsSlideout({
  record,
  onClose,
  onUnlink,
  onRelink,
  onRemoveLink,
  onRelinkLink,
  onApplyChanges,
}: Props) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminLinkedRecordEdit);

  const { data: detailedRecord, isLoading } = useQuery({
    queryKey: adminLinkedRecordsQueryKeys.show(record?.id ?? ''),
    queryFn: ({ signal }) =>
      adminShowLinkedRecord(record!.id, { include: 'locationEpisodeLinks.locationEpisode' }, signal) as Promise<
        LocationEpisodeLinkSet<AdminLinkSetShowFields<'locationEpisodeLinks'>>
      >,
    enabled: !!record?.id,
  });

  const detail = detailedRecord ?? null;
  const scenario = detail ? getScenario(detail) : null;
  const links = detail?.locationEpisodeLinks || [];

  const handleUnlink = () => {
    if (detail) onUnlink(detail);
  };

  const handleRelink = () => {
    if (detail) onRelink(detail);
  };

  const handleApprove = () => {
    if (!detail || !onApplyChanges) return;
    const approveIds = detail.pendingLinks.map((link) => link.id);
    onApplyChanges(approveIds, []);
  };

  const handleDeny = () => {
    if (!detail || !onApplyChanges) return;
    const removeIds = detail.pendingLinks.map((link) => link.id);
    onApplyChanges([], removeIds);
  };

  const handleRemove = (link: LocationEpisodeLink) => {
    if (onRemoveLink) onRemoveLink(link);
  };

  const showNewMatchBanner = detail?.hasNewMatchForExistingLink ?? false;
  const showUnlinkedBanner = scenario === SCENARIOS.UNLINKED;

  const loading = isLoading || !detail || !scenario;

  return (
    <Foldout open={!!record} onBackdropClick={onClose} width='min(80%, 480px)'>
      {record && (
        <PanelContainer>
          <PanelHeader>
            <PanelTitle>Shared Details</PanelTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </PanelHeader>

          {loading ? (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          ) : (
            <>
              <SubHeader>Linked On: {simpleDate(detail.updatedAt)}</SubHeader>
              <PanelContent>
                {showNewMatchBanner && <InfoBanner variant='newMatch' />}
                {showUnlinkedBanner && <InfoBanner variant='unlinked' />}

                <CardsContainer>
                  {links.map((link: LocationEpisodeLink, index: number) => {
                    const showRemove = canEdit && scenario === SCENARIOS.REMOVE_INDIVIDUAL && !link.archived;
                    const showRelink = canEdit && link.archived && (detail?.activeLinks.length ?? 0) >= 2;

                    return (
                      <RecordCard
                        key={link.id}
                        link={link}
                        label={getLabel(index)}
                        showRemove={showRemove}
                        showRelink={showRelink}
                        showPending={link.isPending && !link.archived}
                        isArchived={link.archived}
                        onRemove={() => handleRemove(link)}
                        onRelink={() => onRelinkLink?.(link)}
                      />
                    );
                  })}
                </CardsContainer>
              </PanelContent>

              {canEdit && (
                <SlideoutFooter
                  scenario={scenario}
                  relinkable={detail.relinkable}
                  onUnlink={handleUnlink}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  onRelink={handleRelink}
                />
              )}
            </>
          )}
        </PanelContainer>
      )}
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const SubHeader = styled.div`
  padding: 12px 24px;
  font-size: 12px;
  color: ${colors.black50};
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:first-child) {
    border-top: 1px solid ${colors.black10};
    padding-top: 24px;
    margin-top: 24px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
`;
